import { useState } from "react";
import { HeartHandshake, Heart, Repeat } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { SectionHeading } from "@/components/SectionHeading";
import { api, fetchCollection, formatApiError } from "@/lib/api";

const reveal = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export function SupportBox({ compact = false }) {
  const summary = useQuery({ queryKey: ["support-summary"], queryFn: () => fetchCollection("/support/summary") });
  const presets = summary.data?.presets || [5, 10, 20];
  const tiers = summary.data?.tiers || [];
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [showName, setShowName] = useState(true);
  const [pending, setPending] = useState(null);

  const checkout = async (payload) => {
    setPending(payload.kind + (payload.tier || ""));
    try {
      const { data } = await api.post("/support/checkout", { ...payload, supporter_name: name || null, show_name: showName, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(formatApiError(error));
      setPending(null);
    }
  };
  const finalAmount = custom ? Number(custom) : amount;

  return (
    <div className={`grid gap-6 ${compact ? "" : "lg:grid-cols-2"}`} data-testid="support-box">
      <div className="poster-card p-6 sm:p-8" data-testid="donation-card">
        <p className="eyebrow flex items-center gap-2"><Heart className="h-3 w-3" />Donación puntual</p>
        <h3 className="mt-3 font-display text-4xl uppercase leading-none text-slate-50">Aporta lo que quieras</h3>
        <div className="mt-6 flex flex-wrap gap-2">
          {presets.map((value) => (
            <button key={value} type="button" onClick={() => { setAmount(value); setCustom(""); }} className={`filter-chip text-sm ${!custom && amount === value ? "filter-chip-active" : ""}`} data-testid={`donation-preset-${value}`}>{value} €</button>
          ))}
          <Input type="number" min="1" max="500" value={custom} onChange={(event) => setCustom(event.target.value)} placeholder="Otra cantidad" className="h-10 w-36 rounded-none border-white/10 bg-charcoal text-slate-100 placeholder:text-slate-500" data-testid="donation-custom-input" />
        </div>
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Tu nombre (opcional)" className="mt-4 h-11 rounded-none border-white/10 bg-charcoal text-slate-100 placeholder:text-slate-500" data-testid="support-name-input" />
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={showName} onChange={(event) => setShowName(event.target.checked)} className="accent-clay" data-testid="support-show-name-checkbox" />Mostrar mi nombre entre los que apoyan TODOtiro</label>
        <Button type="button" onClick={() => checkout({ kind: "donation", amount: finalAmount })} disabled={Boolean(pending) || !finalAmount || finalAmount < 1} className="mt-6 h-12 w-full rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid="donation-submit-button">
          {pending === "donation" ? "Abriendo pago seguro..." : `Donar ${finalAmount || ""} €`}
        </Button>
      </div>

      <div className="poster-card p-6 sm:p-8" data-testid="subscription-card">
        <p className="eyebrow flex items-center gap-2"><Repeat className="h-3 w-3" />Apoyo mensual · cancelable cuando quieras</p>
        <h3 className="mt-3 font-display text-4xl uppercase leading-none text-slate-50">Hazte de la casa</h3>
        <div className="mt-6 grid gap-3">
          {tiers.map((tier) => (
            <button key={tier.tier} type="button" onClick={() => checkout({ kind: "subscription", tier: tier.tier })} disabled={Boolean(pending)} className="group flex items-center justify-between gap-4 border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:border-clay/60 hover:bg-clay/10" data-testid={`subscribe-${tier.tier}-button`}>
              <div>
                <p className="font-bold text-slate-50">{tier.label}</p>
                <p className="text-xs text-slate-400">{tier.tier === "patron" ? "Tu nombre en la web, junto a los que sostienen el proyecto" : "Ayudas a mantener la web, el calendario y las crónicas"}</p>
              </div>
              <span className="shrink-0 font-display text-3xl leading-none text-clay">{pending === `subscription${tier.tier}` ? "…" : `${tier.amount} €/mes`}</span>
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">Pago seguro con Stripe. Sin permanencia.</p>
      </div>
    </div>
  );
}

export function SupportSection() {
  const summary = useQuery({ queryKey: ["support-summary"], queryFn: () => fetchCollection("/support/summary") });
  const patrons = summary.data?.patrons || [];
  return (
    <section id="apoyo" className="section-shell bg-panel/45" data-testid="support-section">
      <SectionHeading number="08" eyebrow="Apoya TODOtiro" title="Una web hecha por y para tiradores" description="Publicar tiradas, crónicas y calendario es gratis y así seguirá. Si te resulta útil, puedes ayudar a mantenerla con una donación o un apoyo mensual." />
      <motion.div {...reveal}>
        <SupportBox />
      </motion.div>
      {patrons.length ? (
        <div className="mt-10 border border-white/10 bg-white/[0.03] p-6" data-testid="patrons-list">
          <p className="eyebrow flex items-center gap-2"><HeartHandshake className="h-3 w-3" />Ya apoyan TODOtiro</p>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">{patrons.map((patron, index) => <span key={`${patron}-${index}`}>{patron}</span>)}</p>
        </div>
      ) : null}
    </section>
  );
}
